import "@components/styled-form.css";
import { on, fire } from "delegated-events";
import { handleValidation, handleBlur } from "@common/validation";
import ajax from "@common/ajax";
import scrollTo from "@common/scroll-to";
import { trackGoogleAnalytics } from "@common/util";

const setupDropdowns = async (form) => {
  const dropdowns = form.querySelectorAll(".js-dropdown, .userform .field.dropdown");

  if (!dropdowns.length) {
    return;
  }

  const { default: easydropdown } = await import("easydropdown-a11y");
  import("@styles/components/dropdown.css");

  dropdowns.forEach((el) => {
    const select = el.querySelector("select");

    if (!select) {
      return;
    }

    const placeholder = el.querySelector('option[value=""]');

    if (placeholder && !placeholder.hasAttribute("data-placeholder")) {
      placeholder.setAttribute("data-placeholder", true);

      if (!placeholder.textContent) {
        placeholder.textContent = "Select One";
      }
    }

    const opts = {
      behavior: {
        openOnFocus: true,
        maxVisibleItems: 8,
        liveUpdates: true,
        useNativeUiOnMobile: false,
      },
    };

    easydropdown(select, opts);

    on("change", "#InterestForm_InterestForm_InterestedPosition", function() {
      const otherPositionHolders = document.querySelectorAll("#InterestForm_InterestForm_OtherPosition_Holder");

      otherPositionHolders.forEach(holder => {
          if (this.value === "Other") {
              holder.classList.remove("hidden");
          } else if (!holder.classList.contains("hidden")) {
              holder.classList.add("hidden");
          }
      });
    });
  });
};

const setupMasks = async (form) => {
  const fields = form.querySelectorAll('input[type="tel"], input[data-currency]');

  if (!fields.length) {
    return;
  }

  const { default: IMask } = await import("imask/esm/imask");
  await import("imask/esm/masked/number");
  await import("imask/esm/masked/pattern");

  fields.forEach((field) => {
    if (field.type === "tel") {
      IMask(field, {
        mask: "(000) 000-0000",
      });
    }

    if (field.dataset.currency !== undefined) {
      const mask = IMask(field, {
        mask: Number,
        scale: 0,
        thousandsSeparator: ",",
        min: 1,
      });

      field.addEventListener("updateValue", (e) => {
        field.value = e.detail;
        mask.updateValue();
        field.dispatchEvent(new Event("blur"));
      });
    }
  });
};

const setupSpecialFields = (form) => {
  // Setup file inputs
  form.querySelectorAll("input[type=file]").forEach((input) => {
    const placeholder = document.createElement("div");
    placeholder.classList.add("file-input-placeholder");

    const additionalPlaceholder = input.dataset.additionalPlaceholder;

    if (input.value) {
      placeholder.textContent = input.files[0].name;
    } else {
      let fieldPlaceholder = input.placeholder;

      if (!fieldPlaceholder) {
        fieldPlaceholder = "Select File";
      }

      placeholder.innerHTML = `<span>${fieldPlaceholder}</span>`;

      if (additionalPlaceholder !== undefined) {
        placeholder.innerHTML += additionalPlaceholder;
      }
    }

    input.parentNode.insertBefore(placeholder, input);

    placeholder.addEventListener("click", () => input.click());

    input.addEventListener("change", (e) => {
      if (e.target.files[0]) {
        placeholder.textContent = e.target.files[0].name;
      }
    });
  });

  form.querySelectorAll(".form-field-wrapper.has-length-limit").forEach((field) => {
    const input = field.querySelector(".has-length-limit");

    if (input && input.maxLength) {
      const counter = document.createElement("div");
      counter.classList.add("length-limit-counter");

      const count = document.createElement("span");
      count.textContent = `(${input.value.length}/${input.maxLength})`;

      counter.appendChild(count);

      field.querySelector(".form-field").appendChild(counter);
    }
  });
};

const handleLengthLimit = (e) => {
  const $input = e.target.closest(".has-length-limit");
  const $field = $input.closest(".field.has-length-limit");

  if ($input && $field) {
    const $count = $field.querySelector(".length-limit-counter span");
    $count.textContent = `(${$input.value.length}/${$input.maxLength})`;
  }
};

const handleToggle = (e) => {
  const form = e.target.closest("form");
  const field = e.target;
  const name = e.target.name;
  const selectedValue = e.target.value;

  form.querySelectorAll(`[data-toggle-if^="${name}|"]`).forEach((input) => {
    const toggleGroup = input.closest(".toggle-group");
    let shouldShow = false;

    if (!toggleGroup) {
      return;
    }

    if (field.type === "radio") {
      const targetValues = input.dataset.toggleIf.split("|");
      targetValues.shift();
      shouldShow = targetValues.indexOf(selectedValue) !== -1;
    }

    if (field.type === "checkbox") {
      shouldShow = field.checked;
    }

    if (shouldShow) {
      toggleGroup.classList.remove("hidden");
    } else {
      toggleGroup.classList.add("hidden");

      toggleGroup.querySelectorAll("input:not([type=checkbox]):not([type=radio])").forEach((x) => (x.value = null));

      toggleGroup.querySelectorAll("input:checked").forEach((x) => {
        if (!toggleGroup.classList.contains("ignore-clear")) {
          x.checked = false;
        }

        form.querySelectorAll(`[data-toggle-if^="${x.name}|"]`).forEach((input) => {
          const toggleGroup = input.closest(".toggle-group");
          toggleGroup.classList.add("hidden");

          toggleGroup.querySelectorAll("input:not([type=checkbox]):not([type=radio])").forEach((x) => (x.value = null));
        });
      });
    }
  });
};

const toggleSubmit = (submitButton) => {
  submitButton.disabled = !submitButton.disabled;
  submitButton.classList.toggle("btn-loading");
};

const showFormMessages = (holder, message, success, autoHide) => {
  holder.style.display = "block";
  holder.innerHTML = message;

  if (success) {
    holder.classList.remove("form-message-error");
  } else {
    holder.classList.add("form-message-error");
  }

  if (autoHide) {
    setTimeout(() => {
      holder.style.display = "none";
    }, 5000);
  }
};

const handleAjaxSubmit = (e) => {
  e.preventDefault();

  const form = e.target;
  const isValid = handleValidation(form);

  if (isValid) {
    const formMessages = form.querySelector(".form-messages");
    const submitButton = form.querySelector("[type=submit]");
    let autoHideMessage = form.dataset.autoHideMessage ? form.dataset.autoHideMessage === "true" : true;
    toggleSubmit(submitButton);

    const formData = new FormData(form);

    ajax
      .url(form.action)
      .body(formData)
      .post()
      .json((res) => {
        if (res.success) {
          if (res.ga) {
            res.ga.forEach((event) => trackGoogleAnalytics(event));
          }

          form.reset();
          form.querySelectorAll("select").forEach((x) => (x.value = null));
          form.querySelectorAll(".toggle-fields").forEach((x) => x.classList.add("hidden"));
          form.querySelectorAll(".toggle-group").forEach((x) => x.classList.add("hidden"));

          if (form.dataset.formHideOnSubmit) {
            form.querySelectorAll(".form-content").forEach((x) => x.classList.add("hidden"));
            document.querySelectorAll(".hide-on-form-submit").forEach((x) => x.classList.add("hidden"));
            autoHideMessage = false;

            const shouldScroll = form.dataset.formScroll !== undefined;

            if (shouldScroll) {
              scrollTo(document.body, 32);
            }
          }

          // Allow other scripts to hook into this submit
          fire(document.documentElement, "ajaxform:submit", {
            form: form,
            type: "post",
            url: new URL(form.action),
            queryParameters: new URLSearchParams(formData).toString(),
          });
        }

        document.querySelectorAll("#InterestForm_InterestForm_OtherPosition_Holder").forEach(holder => {
            holder.classList.add("hidden");
        });

        if (res.message) {
          showFormMessages(formMessages, res.message, res.success, autoHideMessage);
        }
      })
      .catch(() => {
        showFormMessages(formMessages, "Sorry, there was a problem with your submission");
      })
      .finally(() => {
        toggleSubmit(submitButton);
      });
  }
};

const handleSubmit = (e) => {
  const form = e.target;
  const isValid = handleValidation(form);
  const submitButton = form.querySelector("[type=submit]");
  toggleSubmit(submitButton);

  if (!isValid) {
    e.preventDefault();
    toggleSubmit(submitButton);
  }
};

const setupForm = (form) => {
  if (form) {
    setupDropdowns(form);
    setupMasks(form);
    setupSpecialFields(form);
  }
};

const init = () => {
  if (!document.documentElement.classList.contains("did-form-init")) {
    document.querySelectorAll(".styled-form").forEach((form) => {
      setupForm(form);
    });

    on("blur", ".has-error", handleBlur, { capture: true });
    on("change", ".has-error", handleBlur, { capture: true });
    on("change", ".has-error blur", handleBlur, {
      capture: true,
    });
    on("submit", ".styled-form[data-form-ajax]", handleAjaxSubmit);
    on("submit", ".styled-form:not([data-form-ajax]):not(.userform)", handleSubmit);
    on("change", "input[type=radio]", handleToggle);
    on("change", "input[type=checkbox]", handleToggle);
    on("keyup", "textarea.has-length-limit", handleLengthLimit, {
      capture: true,
    });

    document.documentElement.classList.add("did-form-init");
  }
};

init();

export { setupForm };
